/* The project's Closeout tab: Facility Navigator's closeout library for the job's district, managed
   from Summit (Duncan, 2026-10-10: PMs "add and manage closeout docs from summit instead of having to
   go to facility navigator every time").

   FN holds every file and folder; nothing is copied. A job links once to its FN project — FN has one
   project per district, so BRYAN-D-01..03 all open "Bryan ISD", as they do in FN. Whatever happens
   here shows up in FN at once, and the customer sees it there.

   The page is FN's own closeout page (app-rebuild apps/web pages/CloseoutDocsPage.tsx,
   closeoutShared.tsx, closeout.css), ported rather than redesigned so a PM who knows one knows the
   other: the coverage strip, Browse / Upload history, the Finder-style columns, the details pane, Quick
   Look (Space), and FN's chunked uploader (./closeoutUploads.js). Summit's server decides who may do
   what (server/routes/closeout.js); this page only shows what that answers. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCloseout, getCloseoutFnProjects, linkCloseout, renameCloseoutDoc, moveCloseoutDoc, deleteCloseoutDoc,
  getCloseoutDocLink, createCloseoutFolder, updateCloseoutFolder, deleteCloseoutFolder, getCloseoutZipLink,
} from '../../api';
import {
  startUploads, subscribeUploads, retryUpload, dismissBatch, pickedFromEntries, pickedFromInput, videoTypeOfName, MAX_BATCH,
} from './closeoutUploads';
import { usePageSearch, matchesSearch, noMatchText } from '../../lib/pageSearch';
import { useCloseoutSend, SubsDocumentsPicker, SendProgress } from '../subs/SendToCloseout';

const FN_CLOSEOUT_URL = 'https://navigator.e3es.com/closeout';
const ROOT_FOLDER_LABEL = 'Top level';
const UNFILED = 'unfiled';
// A thumbnail is not worth an unbounded download (O&M manuals run to tens of MB). Past this the pane
// offers the preview instead; Quick Look still opens the real thing.
const PREVIEW_AUTO_MAX = 25 * 1024 * 1024;

/* ── glyphs (FN's, so the two pages look alike) ── */
const GL = {
  folder: '<path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h3.8a1.5 1.5 0 0 1 1.1.5l1.2 1.3a1.5 1.5 0 0 0 1.1.5H19A1.5 1.5 0 0 1 20.5 9v9A1.5 1.5 0 0 1 19 19.5H5A1.5 1.5 0 0 1 3.5 18Z"/>',
  folderPlus: '<path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h3.8a1.5 1.5 0 0 1 1.1.5l1.2 1.3a1.5 1.5 0 0 0 1.1.5H19A1.5 1.5 0 0 1 20.5 9v9A1.5 1.5 0 0 1 19 19.5H5A1.5 1.5 0 0 1 3.5 18Z"/><path d="M12 11v5M9.5 13.5h5"/>',
  refresh: '<path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 4v5h-5"/>',
  upload: '<path d="M12 20V8"/><path d="m7.5 12.5 4.5-4.5 4.5 4.5"/><path d="M4.5 4.5h15"/>',
  download: '<path d="M12 3v12"/><path d="m7.5 10.5 4.5 4.5 4.5-4.5"/><path d="M4.5 20.5h15"/>',
  trash: '<path d="M4 7h16M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7M6.5 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7"/>',
  pencil: '<path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z"/><path d="M14.5 6.5l3 3"/>',
  chevron: '<path d="M9 6l6 6-6 6"/>',
  check: '<circle cx="12" cy="12" r="9"/><path d="m8.4 12.2 2.4 2.4 4.8-4.8"/>',
  file: '<path d="M6.5 3.5h7l4 4V19a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 5 19V5a1.5 1.5 0 0 1 1.5-1.5Z"/><path d="M13 3.5V8h4.5"/>',
  fileText: '<path d="M6.5 3.5h7l4 4V19a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 5 19V5a1.5 1.5 0 0 1 1.5-1.5Z"/><path d="M13 3.5V8h4.5"/><path d="M8.5 12.5h7M8.5 16h4.5"/>',
  sheet: '<rect x="4" y="4" width="16" height="16" rx="2"/><path d="M4 10h16M4 15h16M10 4v16"/>',
  image: '<rect x="3" y="4.5" width="18" height="15" rx="2"/><circle cx="8.5" cy="10" r="1.7"/><path d="m4 17 5-4 4 3 3-2 4 3.5"/>',
  expand: '<path d="M9 4H4v5M15 4h5v5M15 20h5v-5M9 20H4v-5"/>',
  close: '<path d="M6 6l12 12M18 6L6 18"/>',
  link: '<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/>',
  external: '<path d="M14 4h6v6"/><path d="M20 4 11 13"/><path d="M18 14v4.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  inbox: '<path d="M4 13h4l1.5 3h5L16 13h4"/><path d="M4.5 13 7 5h10l2.5 8v5A1.5 1.5 0 0 1 18 19.5H6A1.5 1.5 0 0 1 4.5 18Z"/>',
  alert: '<path d="M12 4 2.8 19.5h18.4Z"/><path d="M12 10v4.5M12 17.2v.3"/>',
  users: '<circle cx="9" cy="8.5" r="3.2"/><path d="M3.5 19.5v-1a4.5 4.5 0 0 1 4.5-4.5h2a4.5 4.5 0 0 1 4.5 4.5v1"/><path d="M15.5 5.6a3.2 3.2 0 0 1 0 5.8M17.5 14.2a4.5 4.5 0 0 1 3 4.3v1"/>',
};
const KIND_GLYPH = {
  'as-builts': '<path d="M4 16.5 16.5 4l3.5 3.5L7.5 20H4Z"/><path d="M13.5 7 17 10.5"/>',
  'o&m manuals': '<path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5Z"/><path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5a1.5 1.5 0 0 0 1.5-1.5Z"/>',
  warranties: '<circle cx="12" cy="9" r="5"/><path d="m9 13.5-1 6.5 4-2 4 2-1-6.5"/>',
  'start-up sheets': '<rect x="4" y="4" width="16" height="16" rx="2"/><path d="M4 10h16M4 15h16M10 4v16"/>',
  other: GL.file,
};
const kindGlyph = (name) => KIND_GLYPH[String(name || '').trim().toLowerCase()] || GL.folder;

const Glyph = ({ d, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: d }} />
);

/* ── helpers ── */
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const dayLabel = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
function formatBytes(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  const kb = n / 1024; if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024; if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}
function fileGlyph(name, mime) {
  const ext = (String(name || '').split('.').pop() || '').toLowerCase();
  const m = mime || '';
  if (ext === 'pdf' || m === 'application/pdf') return GL.fileText;
  if (['xlsx', 'xls', 'xlsm', 'csv', 'tsv'].includes(ext) || m.includes('spreadsheet')) return GL.sheet;
  if (['doc', 'docx', 'rtf', 'txt'].includes(ext) || m.includes('word')) return GL.fileText;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'tif', 'tiff', 'svg'].includes(ext) || m.startsWith('image/')) return GL.image;
  return GL.file;
}
/** FN's preview allowlist (its server is the real gate: anything else answers 415). */
const PREVIEWABLE = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp',
  'text/plain', 'text/csv', 'video/mp4', 'video/webm', 'video/quicktime']);
function previewKind(contentType, fileName) {
  let t = String(contentType || '').split(';')[0].trim().toLowerCase();
  if (!t || t === 'application/octet-stream') t = videoTypeOfName(fileName) || t;
  if (!t || !PREVIEWABLE.has(t)) return null;
  return t.startsWith('image/') ? 'image' : t.startsWith('video/') ? 'video' : 'frame';
}
/** Middle truncation: the head ellipsises, the tail stays (document names differ at the END). */
function splitTail(name, tailChars = 14) {
  const chars = Array.from(name);
  if (chars.length <= tailChars + 6) return [name, ''];
  return [chars.slice(0, chars.length - tailChars).join(''), chars.slice(chars.length - tailChars).join('')];
}
const MidName = ({ text }) => { const [h, t] = splitTail(text); return <span className="co-cname" title={text}><i>{h}</i><u>{t}</u></span>; };
const EndName = ({ text }) => <span className="co-cname" title={text}><i style={{ flex: 1 }}>{text}</i></span>;

/** A document's kind is its TOP-LEVEL folder. Bounded, so bad data can never spin. */
function topLevelOf(folderId, byId) {
  let cur = folderId;
  for (let i = 0; cur && i < 24; i++) {
    const f = byId.get(cur);
    if (!f) return null;
    if (!f.parentId) return f.id;
    cur = f.parentId;
  }
  return null;
}
function pathBelowKind(folderId, byId) {
  const segs = [];
  for (let cur = folderId, i = 0; cur && i < 24; i++) {
    const f = byId.get(cur);
    if (!f || !f.parentId) break;
    segs.unshift(f.name);
    cur = f.parentId;
  }
  return segs.join(' / ');
}
function secondLevelOf(folderId, byId) {
  const chain = [];
  for (let cur = folderId, i = 0; cur && i < 24; i++) {
    const f = byId.get(cur);
    if (!f) return '';
    chain.unshift(f);
    cur = f.parentId;
  }
  return (chain[1] && chain[1].name) || '';
}

/** Customer and partner uploads carry a marker (FN, 2026-10-07). E3's own carry none — they are most
 *  of every library — and everyone in Summit is E3, so FN's "You uploaded" (a customer's chip) never shows. */
const UploaderChip = ({ doc }) => {
  if (doc.uploaderKind === 'customer') return <span className="co-upby customer">Customer</span>;
  if (doc.uploaderKind === 'partner') return <span className="co-upby partner">Partner</span>;
  return null;
};

/* ── coverage: which kinds have arrived ── */
function useKinds(folders, documents) {
  return useMemo(() => {
    const byId = new Map(folders.map((f) => [f.id, f]));
    const tops = folders.filter((f) => !f.parentId);
    const blank = () => ({ files: 0, newest: null, bytes: 0 });
    const acc = new Map(tops.map((f) => [f.id, blank()]));
    const unfiled = blank();
    for (const d of documents) {
      const top = topLevelOf(d.folderId, byId);
      const bucket = top ? acc.get(top) : unfiled;
      if (!bucket) continue;
      bucket.files += 1;
      bucket.bytes += d.sizeBytes || 0;
      if (!bucket.newest || d.createdAt > bucket.newest) bucket.newest = d.createdAt;
    }
    const isOther = (n) => n.trim().toLowerCase() === 'other';
    const ordered = [...tops.filter((f) => !isOther(f.name)), ...tops.filter((f) => isOther(f.name))];
    const tiles = ordered.map((f) => ({ id: f.id, name: f.name, unfiled: false, ...acc.get(f.id) }));
    if (unfiled.files > 0) tiles.push({ id: UNFILED, name: 'Unfiled', unfiled: true, ...unfiled });
    return { tiles, byId };
  }, [folders, documents]);
}

function CoverageCard({ tiles, selected, onSelect, onAddKind }) {
  const [adding, setAdding] = useState(null);
  const kinds = tiles.filter((t) => !t.unfiled);
  const withDocs = kinds.filter((t) => t.files > 0).length;
  const files = tiles.reduce((n, t) => n + t.files, 0);
  const bytes = tiles.reduce((n, t) => n + t.bytes, 0);
  return (
    <div className="co-covcard">
      <div className="co-covhead">
        <b>{files > 0 ? 'Received so far' : 'What this closeout package will contain'}</b>
        <span>{files > 0
          ? `${withDocs} of ${kinds.length} kind${kinds.length === 1 ? '' : 's'} have documents · ${files} file${files === 1 ? '' : 's'} · ${formatBytes(bytes)}`
          : `${kinds.length} kind${kinds.length === 1 ? '' : 's'} · nothing handed over yet`}</span>
      </div>
      <div className="co-covtiles">
        {tiles.map((t) => {
          const on = selected === t.id;
          const empty = t.files === 0;
          return (
            <button key={t.id} type="button" aria-pressed={on}
              className={'co-covtile' + (empty ? ' empty' : '') + (t.unfiled ? ' unfiled' : '') + (on ? ' on' : '')}
              onClick={() => onSelect(on ? null : t.id)}>
              <Glyph d={t.unfiled ? GL.inbox : kindGlyph(t.name)} size={16} />
              <span className="co-ctname">{t.name}</span>
              <span className="co-ctsub">
                {t.unfiled ? `${t.files} file${t.files === 1 ? '' : 's'} · outside any kind`
                  : empty ? 'No files yet' : `${t.files} file${t.files === 1 ? '' : 's'} · ${t.newest ? dayLabel(t.newest) : ''}`}
              </span>
            </button>
          );
        })}
        {onAddKind && (adding === null ? (
          <button type="button" className="co-covtile add" onClick={() => setAdding('')}>
            <Glyph d={GL.plus} size={16} /><span className="co-ctname">Add kind</span>
          </button>
        ) : (
          <form className="co-covtile add editing" onSubmit={(e) => { e.preventDefault(); const n = adding.trim(); if (n) onAddKind(n); setAdding(null); }}>
            <input autoFocus value={adding} placeholder="Kind name" aria-label="New kind name"
              onChange={(e) => setAdding(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setAdding(null); } }}
              onBlur={() => setAdding(null)} />
          </form>
        ))}
      </div>
    </div>
  );
}

/* ── upload history: documents grouped by day + uploader (FN's handovers) ── */
function useHandovers(documents, byId) {
  return useMemo(() => {
    const groups = new Map();
    for (const d of documents) {
      const key = `${String(d.createdAt).slice(0, 10)}|${d.uploadedByName || ''}`;
      const g = groups.get(key) || { key, date: d.createdAt, uploader: d.uploadedByName, lead: d, docs: [], areas: [] };
      g.docs.push(d);
      if (d.createdAt > g.date) g.date = d.createdAt;
      groups.set(key, g);
    }
    for (const g of groups.values()) {
      const seen = new Set();
      for (const d of g.docs) {
        const a = d.folderId == null ? 'Unfiled' : secondLevelOf(d.folderId, byId);
        if (a && !seen.has(a)) { seen.add(a); g.areas.push(a); }
      }
      g.docs.sort((x, y) => x.fileName.localeCompare(y.fileName));
    }
    return [...groups.values()].sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [documents, byId]);
}

const extOf = (name) => { const i = name.lastIndexOf('.'); return i > 0 ? name.slice(i + 1).toUpperCase().slice(0, 4) : 'FILE'; };

function HandoverList({ handovers, byId, showKind, onOpen, onDownload }) {
  return (
    <div className="co-hovlist">
      {handovers.map((h) => {
        const outside = h.lead.uploaderKind === 'customer' || h.lead.uploaderKind === 'partner';
        return (
          <div className="co-hov" key={h.key}>
            <div className="co-hovgut">
              <div className="co-hovdate">{shortDate(h.date)}</div>
              <div className="co-hovwho">
                {h.docs.length} document{h.docs.length === 1 ? '' : 's'}
                {h.uploader ? (outside ? <> · {h.uploader} <UploaderChip doc={h.lead} /></> : ` · ${h.uploader}, E3`) : ''}
              </div>
              {h.areas.length > 0 && <div className="co-hovarea">{h.areas.join(' · ')}</div>}
            </div>
            <div className="co-hovcard">
              {h.docs.map((d) => {
                const canRead = previewKind(d.contentType, d.fileName) !== null;
                const kind = d.folderId == null ? 'Unfiled' : ((byId.get(topLevelOf(d.folderId, byId) || '') || {}).name || '');
                const path = d.folderId == null ? '' : pathBelowKind(d.folderId, byId);
                const bits = [showKind ? kind : '', path, d.description || ''].filter(Boolean);
                return (
                  <div key={d.id} className={'co-hovrow' + (canRead ? ' readable' : '')}
                    onClick={canRead ? () => onOpen(d) : undefined} role={canRead ? 'button' : undefined} tabIndex={canRead ? 0 : undefined}
                    onKeyDown={canRead ? (e) => { if (e.key === 'Enter') { e.preventDefault(); onOpen(d); } } : undefined}>
                    <span className="co-hovext">{extOf(d.fileName)}</span>
                    <div className="co-hovname">
                      <span title={d.fileName}>{d.fileName}</span>
                      {bits.length > 0 && <span className="co-hovsub">{bits.join(' · ')}</span>}
                    </div>
                    <span className="co-hovsize">{formatBytes(d.sizeBytes)}</span>
                    {canRead
                      ? <span className="co-hovact read">Read</span>
                      : <button type="button" className="co-hovact" onClick={(e) => { e.stopPropagation(); onDownload(d); }}>Download</button>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── a destructive confirm with a typed token (Settings' dialog, so deleting looks the same) ── */
function DangerDialog({ open, title, warning, token, confirmLabel, busy, error, onClose, onConfirm }) {
  const [typed, setTyped] = useState('');
  useEffect(() => { if (open) setTyped(''); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);
  if (!open) return null;
  const locked = typed.trim().toLowerCase() !== String(token).trim().toLowerCase();
  return (
    <div className="co-dlgback" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="co-dlg" role="dialog" aria-modal="true" aria-label={title}>
        <div className="co-dlgicon"><Glyph d={GL.trash} size={17} /></div>
        <div className="co-dlgtitle">{title}</div>
        <div className="co-dlgbody">{warning}</div>
        <div className="co-dlgtype">Type <b>{token}</b> to confirm</div>
        <input className="co-txt" value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus disabled={busy} spellCheck={false}
          aria-label={`Type ${token} to confirm`} placeholder={token} style={{ width: '100%' }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !locked && !busy) onConfirm(); }} />
        {error && <div className="co-dlgerr" role="alert">{error}</div>}
        <div className="co-dlgbtns">
          <button type="button" className="co-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="co-btn danger" disabled={busy || locked} onClick={onConfirm}>
            <Glyph d={GL.trash} />{busy ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── link this job to its FN library ── */
function LinkCard({ projectId, project, suggestion, current, onLinked, onCancel }) {
  const [options, setOptions] = useState(null);
  const [choice, setChoice] = useState((current && current.id) || (suggestion && suggestion.id) || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    getCloseoutFnProjects(projectId).then((list) => { if (alive) setOptions(list); }).catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [projectId]);
  const link = async () => {
    if (!choice) return;
    setBusy(true); setError(null);
    try { await linkCloseout(projectId, choice); onLinked(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const real = (options || []).filter((p) => !p.isTest);
  const tests = (options || []).filter((p) => p.isTest);
  return (
    <div className="co-linkcard">
      <span className="co-linkico"><Glyph d={GL.link} size={18} /></span>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div className="co-linktitle">{current ? 'Change the Facility Navigator library' : `Link ${project.project_number || 'this job'} to its Facility Navigator project`}</div>
        <div className="co-linksub">
          {current ? 'Pick the district whose closeout library this job should show.'
            : suggestion ? `Suggested from the job number. Every ${suggestion.name} job shares one library, the same as in Facility Navigator.`
              : 'Pick the district. Jobs at the same district share one library, the same as in Facility Navigator.'}
        </div>
        {error && <div className="co-inlineerr" role="alert">{error}</div>}
      </div>
      <select className="co-sel" value={choice} onChange={(e) => setChoice(e.target.value)} disabled={busy || !options} aria-label="Facility Navigator project" style={{ minWidth: 200 }}>
        <option value="">{options ? 'Choose a district…' : 'Loading…'}</option>
        {suggestion && !real.some((p) => p.id === suggestion.id) && <option value={suggestion.id}>{suggestion.name}</option>}
        {real.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        {tests.length > 0 && (
          <optgroup label="Test and demo">
            {tests.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </optgroup>
        )}
      </select>
      {onCancel && <button type="button" className="co-btn" disabled={busy} onClick={onCancel}>Cancel</button>}
      <button type="button" className="co-btn primary" disabled={busy || !choice} onClick={link}>{busy ? 'Linking…' : 'Link'}</button>
    </div>
  );
}

/* ── uploads in flight for this job (the queue outlives the tab) ── */
function UploadRows({ projectId }) {
  const [batches, setBatches] = useState([]);
  useEffect(() => subscribeUploads((all) => setBatches(all.filter((b) => b.projectId === projectId))), [projectId]);
  if (!batches.length) return null;
  return (
    <div className="co-uplist">
      {batches.map((b) => {
        const done = b.rows.every((r) => r.status === 'done' || r.status === 'error');
        const okCount = b.rows.filter((r) => r.status === 'done').length;
        return (
          <div key={b.id} className="co-upbatch">
            <div className="co-uphead">
              <b>{done ? `Uploaded ${okCount} of ${b.rows.length}` : `Uploading ${b.rows.length} file${b.rows.length === 1 ? '' : 's'}`}</b>
              <span>{b.label}</span>
              {done && <button type="button" className="co-linkbtn" onClick={() => dismissBatch(b.id)}>Dismiss</button>}
            </div>
            {b.rows.map((r) => (
              <div key={r.id} className="co-uplrow">
                <div className="co-un">
                  <span title={r.name}>{r.name}</span>
                  <div className={'co-bar' + (r.status === 'error' ? ' err' : '')}><i style={{ width: `${r.status === 'error' ? 100 : r.pct}%` }} /></div>
                  {r.status === 'error' && (
                    <div className="co-uplerr">{r.error}<button type="button" onClick={() => retryUpload(r.id)}>Retry</button></div>
                  )}
                </div>
                <span className={'co-uplpct' + (r.status === 'error' ? ' err' : '')}>
                  {r.status === 'waiting' ? 'Waiting' : r.status === 'error' ? 'Failed' : r.status === 'done' ? 'Done' : `${r.pct}%`}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* ── the tab ── */
export default function CloseoutTab({ projectId, project }) {
  const [info, setInfo] = useState(null); // { link, canManage, suggestion, tree, error }
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [actionErr, setActionErr] = useState(null);
  const [changingLink, setChangingLink] = useState(false);
  const [path, setPath] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [mode, setMode] = useState('browse');
  const [selectedKind, setSelectedKind] = useState(null);
  const [busy, setBusy] = useState(false);

  const reqSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++reqSeq.current; // a slower earlier answer must not overwrite a newer one
    setLoading(true); setLoadErr(null);
    try {
      const fresh = await getCloseout(projectId);
      if (seq !== reqSeq.current) return;
      setInfo(fresh);
      const tree = fresh.tree;
      if (tree) {
        // Drop open columns for folders that are gone or have moved (someone else's change, or ours).
        const byId = new Map(tree.folders.map((f) => [f.id, f]));
        setPath((prev) => {
          const kept = [];
          for (let i = 0; i < prev.length; i++) {
            const f = byId.get(prev[i]);
            if (!f || (f.parentId || null) !== (i === 0 ? null : kept[i - 1] || null)) break;
            kept.push(prev[i]);
          }
          return kept.length === prev.length ? prev : kept;
        });
        setSelectedDocId((cur) => (cur && tree.documents.some((d) => d.id === cur) ? cur : null));
      }
    } catch (e) {
      if (seq === reqSeq.current) setLoadErr(e.message);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPath([]); setSelectedDocId(null); }, [projectId]);
  // Add from the subs (Duncan 2026-10-25): the subs' documents on E3 Subs, sent into the folder open here.
  const [fromSubs, setFromSubs] = useState(false);
  const subsSend = useCloseoutSend(projectId, load);

  const canManage = !!(info && info.canManage);
  const tree = info && info.tree;
  const folders = useMemo(() => (tree ? tree.folders : []), [tree]);
  const documents = useMemo(() => (tree ? tree.documents : []), [tree]);
  const libName = (info && info.link && info.link.name) || 'Library';

  const foldersByParent = useMemo(() => {
    const m = new Map();
    for (const f of folders) { const k = f.parentId || null; if (!m.has(k)) m.set(k, []); m.get(k).push(f); }
    for (const list of m.values()) list.sort((a, b) => a.name.localeCompare(b.name));
    return m;
  }, [folders]);
  const docsByFolder = useMemo(() => {
    const m = new Map();
    for (const d of documents) { const k = d.folderId || null; if (!m.has(k)) m.set(k, []); m.get(k).push(d); }
    for (const list of m.values()) list.sort((a, b) => a.fileName.localeCompare(b.fileName));
    return m;
  }, [documents]);
  const folderById = useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders]);
  // The top bar's search (lib/pageSearch): a document matches on its name, note, uploader or the
  // folders it sits in. Upload history lists the matches; Browse keeps every folder on the way to
  // one, so each match can still be reached. The strip, coverage tiles and Download all keep the library.
  const search = usePageSearch('Search closeout documents…');
  const searchHits = useMemo(() => {
    if (!search.trim()) return null;
    const place = (folderId) => [(folderById.get(topLevelOf(folderId, folderById)) || {}).name, pathBelowKind(folderId, folderById)];
    const docs = new Set(documents.filter((d) => matchesSearch(search, d.fileName, d.description, d.uploadedByName, d.uploaderKind,
      d.folderId == null ? 'Unfiled' : place(d.folderId))).map((d) => d.id));
    const keep = new Set();
    const climb = (id) => { for (let cur = id, i = 0; cur && i < 24 && !keep.has(cur); i++) { keep.add(cur); cur = (folderById.get(cur) || {}).parentId; } };
    documents.forEach((d) => { if (docs.has(d.id)) climb(d.folderId); });
    folders.forEach((f) => { if (matchesSearch(search, place(f.id))) climb(f.id); });
    return { docs, folders: keep };
  }, [search, documents, folders, folderById]);
  const childCount = useCallback((id) => ((foldersByParent.get(id) || []).length + (docsByFolder.get(id) || []).length), [foldersByParent, docsByFolder]);
  const columns = useMemo(() => [null, ...path].map((parentId) => ({
    parentId,
    title: parentId ? ((folderById.get(parentId) || {}).name || '…') : ROOT_FOLDER_LABEL,
    folders: (foldersByParent.get(parentId) || []).filter((f) => !searchHits || searchHits.folders.has(f.id)),
    docs: (docsByFolder.get(parentId) || []).filter((d) => !searchHits || searchHits.docs.has(d.id)),
  })), [path, foldersByParent, docsByFolder, folderById, searchHits]);

  const selectedDoc = useMemo(() => documents.find((d) => d.id === selectedDocId) || null, [documents, selectedDocId]);
  const currentFolderId = path.length ? path[path.length - 1] : null;
  const totalBytes = useMemo(() => documents.reduce((a, d) => a + (d.sizeBytes || 0), 0), [documents]);
  const lastUpdated = useMemo(() => (documents.length ? documents.reduce((m, d) => (d.createdAt > m ? d.createdAt : m), documents[0].createdAt) : null), [documents]);

  const openFolder = useCallback((ci, folderId) => { setPath((p) => [...p.slice(0, ci), folderId]); setSelectedDocId(null); }, []);
  const openDoc = useCallback((ci, docId) => { setPath((p) => p.slice(0, ci)); setSelectedDocId(docId); }, []);

  const { tiles: kindTiles, byId: kindFolderById } = useKinds(folders, documents);
  const shownDocs = useMemo(() => {
    const hits = searchHits ? documents.filter((d) => searchHits.docs.has(d.id)) : documents;
    if (mode !== 'handovers' || !selectedKind) return hits;
    if (selectedKind === UNFILED) return hits.filter((d) => d.folderId == null);
    return hits.filter((d) => topLevelOf(d.folderId, kindFolderById) === selectedKind);
  }, [mode, selectedKind, documents, kindFolderById, searchHits]);
  const handovers = useHandovers(shownDocs, kindFolderById);

  /* ── links to files: fetched from FN through Summit, short-lived, cached briefly ── */
  const linkCache = useRef(new Map());
  const docLink = useCallback(async (docId, kind) => {
    const key = `${docId}/${kind}`;
    const hit = linkCache.current.get(key);
    if (hit && hit.at > Date.now() - 120000) return hit.url;
    const out = await getCloseoutDocLink(projectId, docId, kind);
    linkCache.current.set(key, { url: out.url, at: Date.now() });
    return out.url;
  }, [projectId]);
  const download = useCallback(async (doc) => {
    setActionErr(null);
    try {
      const url = await docLink(doc.id, 'download');
      // The link answers with "save as", so the page stays where it is.
      const a = document.createElement('a');
      // FN's link says "save as" itself; `download` (ignored across origins) is for same-origin links.
      a.href = url; a.rel = 'noopener'; a.download = doc.fileName; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setActionErr(e.message); }
  }, [docLink]);
  const [zipBusy, setZipBusy] = useState(false);
  const downloadAll = useCallback(async () => {
    setActionErr(null); setZipBusy(true);
    try {
      const out = await getCloseoutZipLink(projectId);
      const a = document.createElement('a');
      a.href = out.url; a.rel = 'noopener'; a.download = `${libName} closeout documents.zip`; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      // FN: 404 = nothing in the library; 410 = the one-use, five-minute link was already spent.
      setActionErr(e.status === 404 ? 'There’s nothing in this library to download yet.' : e.message);
    } finally { setZipBusy(false); }
  }, [projectId, libName]);

  /* ── preview: the details thumbnail, and Quick Look ── */
  const preview = selectedDoc ? previewKind(selectedDoc.contentType, selectedDoc.fileName) : null;
  const heavy = !!selectedDoc && (selectedDoc.sizeBytes || 0) > PREVIEW_AUTO_MAX;
  const [previewUrl, setPreviewUrl] = useState(null); // { id, url }
  const [quickLook, setQuickLook] = useState(false);
  useEffect(() => {
    setPreviewUrl(null);
    if (!selectedDoc || !preview || (heavy && !quickLook)) return undefined;
    let alive = true;
    docLink(selectedDoc.id, 'preview')
      .then((url) => { if (alive) setPreviewUrl({ id: selectedDoc.id, url }); })
      .catch((e) => { if (alive) { setQuickLook(false); setActionErr(e.message); } });
    return () => { alive = false; };
  }, [selectedDoc, preview, heavy, quickLook, docLink]);
  useEffect(() => { if (!preview) setQuickLook(false); }, [preview]);
  const shownPreview = previewUrl && selectedDoc && previewUrl.id === selectedDoc.id ? previewUrl.url : null;

  const siblingDocs = useMemo(() => {
    if (!selectedDoc) return [];
    if (mode === 'handovers') return handovers.flatMap((h) => h.docs);
    return docsByFolder.get(selectedDoc.folderId || null) || [];
  }, [mode, handovers, docsByFolder, selectedDoc]);

  // Space to look, Esc to close, ←/→ to step while looking — the Finder gesture, as in FN.
  const keyState = useRef({});
  keyState.current = { quickLook, selectedDoc, preview, siblingDocs };
  const dialogOpenRef = useRef(false);
  useEffect(() => {
    const onKey = (e) => {
      if (dialogOpenRef.current) return;
      const k = keyState.current;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.key === 'Escape' && k.quickLook) { e.preventDefault(); setQuickLook(false); return; }
      if (e.key === ' ' && k.selectedDoc && k.preview) {
        // Only from a document row (or nothing focused) — on any other button Space must press it.
        if (t && t.tagName === 'BUTTON' && !t.hasAttribute('data-docrow')) return;
        if (t && (t.tagName === 'A' || t.tagName === 'SUMMARY')) return;
        if (t && t !== document.body && !(t.tagName === 'BUTTON')) return;
        e.preventDefault();
        setQuickLook((v) => !v);
        return;
      }
      if (!k.quickLook || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      const sibs = k.siblingDocs;
      const i = sibs.findIndex((d) => d.id === (k.selectedDoc && k.selectedDoc.id));
      const n = i + (e.key === 'ArrowRight' ? 1 : -1);
      if (i < 0 || n < 0 || n >= sibs.length) return;
      e.preventDefault();
      setSelectedDocId(sibs[n].id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* ── uploads ── */
  const [scanning, setScanning] = useState(false);
  const [dropTarget, setDropTarget] = useState(null); // folder id | 'none' (top level) | null
  const fileRef = useRef(null);
  const folderRef = useRef(null);
  const uploadTargetRef = useRef(null);

  /** Find-or-create one folder; a batch-local cache stops a 40-file drop racing itself into duplicates. */
  const ensureFolder = useCallback(async (parentId, name, cache) => {
    const key = `${parentId || '~'}/${name.toLowerCase()}`;
    if (cache.has(key)) return cache.get(key);
    const existing = (foldersByParent.get(parentId) || []).find((f) => f.name.toLowerCase() === name.toLowerCase());
    if (existing) { cache.set(key, existing.id); return existing.id; }
    try {
      const created = await createCloseoutFolder(projectId, parentId, name);
      cache.set(key, created.id);
      return created.id;
    } catch (e) {
      // Someone else made it first (409): read the library and use theirs.
      const fresh = await getCloseout(projectId);
      const found = ((fresh.tree && fresh.tree.folders) || []).find((f) => (f.parentId || null) === parentId && f.name.toLowerCase() === name.toLowerCase());
      if (!found) throw e;
      cache.set(key, found.id);
      return found.id;
    }
  }, [foldersByParent, projectId]);

  const uploadPicked = useCallback((picked, targetFolderId) => {
    if (!picked.length) { setActionErr('Nothing to upload — that folder has no files in it.'); return; }
    if (picked.length > MAX_BATCH) { setActionErr(`That's ${picked.length} files — upload at most ${MAX_BATCH} at a time.`); return; }
    setActionErr(null);
    const cache = new Map();
    const folderFor = async (dir) => {
      let parent = targetFolderId;
      for (const seg of dir.split('/').filter(Boolean)) parent = await ensureFolder(parent, seg, cache);
      return parent;
    };
    const folderName = targetFolderId ? (folderById.get(targetFolderId) || {}).name : null;
    startUploads({ projectId, label: [libName, folderName].filter(Boolean).join(' › '), picked, folderFor, onLanded: () => load() });
  }, [projectId, libName, folderById, ensureFolder, load]);

  const onDropInto = useCallback((e, folderId) => {
    e.preventDefault(); e.stopPropagation(); setDropTarget(null);
    if (!canManage) return;
    // Entries must be read now — the drop's items are emptied once this handler returns.
    const entries = Array.from((e.dataTransfer && e.dataTransfer.items) || [])
      .map((i) => (typeof i.webkitGetAsEntry === 'function' ? i.webkitGetAsEntry() : null)).filter(Boolean);
    const files = e.dataTransfer && e.dataTransfer.files;
    setScanning(true);
    pickedFromEntries(entries, files).then((p) => uploadPicked(p, folderId)).catch((er) => setActionErr(er.message)).finally(() => setScanning(false));
  }, [canManage, uploadPicked]);
  const dragOver = (key) => (e) => { if (canManage) { e.preventDefault(); e.stopPropagation(); setDropTarget(key); } };
  const pickInto = (folderId, kind) => { uploadTargetRef.current = folderId; const el = (kind === 'files' ? fileRef : folderRef).current; if (el) el.click(); };

  /* ── folders and documents ── */
  const [renaming, setRenaming] = useState(null);       // folder: { id, text }
  const [renamingDoc, setRenamingDoc] = useState(null); // document: { id, text }
  const [movingDocId, setMovingDocId] = useState(null);
  const [confirmFolder, setConfirmFolder] = useState(null);
  const [confirmDoc, setConfirmDoc] = useState(null);
  const [confirmErr, setConfirmErr] = useState(null);
  dialogOpenRef.current = !!confirmFolder || !!confirmDoc;

  const run = useCallback(async (fn) => {
    setBusy(true); setActionErr(null);
    try { await fn(); } catch (e) { setActionErr(e.message); } finally { setBusy(false); }
  }, []);

  // Made with a free name and straight into the inline rename — no prompt() (it freezes the page).
  const newFolder = (parentId) => run(async () => {
    const taken = new Set(folders.filter((f) => (f.parentId || null) === parentId).map((f) => f.name.trim().toLowerCase()));
    let name = 'New folder';
    for (let n = 2; taken.has(name.toLowerCase()); n++) name = `New folder ${n}`;
    const created = await createCloseoutFolder(projectId, parentId, name);
    await load();
    setRenaming({ id: created.id, text: created.name });
  });
  const submitRename = () => {
    if (!renaming) return;
    const name = renaming.text.trim();
    const current = (folderById.get(renaming.id) || {}).name || '';
    if (!name || name === current) { setRenaming(null); return; }
    run(async () => { await updateCloseoutFolder(projectId, renaming.id, { name }); setRenaming(null); await load(); });
  };
  const submitDocRename = () => {
    if (!renamingDoc) return;
    const name = renamingDoc.text.trim();
    const doc = documents.find((d) => d.id === renamingDoc.id);
    if (!name || !doc || name === doc.fileName) { setRenamingDoc(null); return; }
    run(async () => { await renameCloseoutDoc(projectId, renamingDoc.id, name); linkCache.current.clear(); setRenamingDoc(null); await load(); });
  };
  // An empty folder goes at once; one with contents comes back FOLDER_NOT_EMPTY and asks for its name,
  // because forcing it deletes every document inside — for the customer too, with no undo.
  const removeFolder = (id) => {
    const f = folderById.get(id);
    if (!f) return;
    run(async () => {
      try {
        await deleteCloseoutFolder(projectId, id, false);
        setPath((p) => p.filter((x) => x !== id));
        await load();
      } catch (e) {
        if (e.code !== 'FOLDER_NOT_EMPTY') throw e;
        setConfirmErr(null);
        setConfirmFolder({ id, name: f.name, documents: (e.body && e.body.documents) || 0, folders: (e.body && e.body.folders) || 0 });
      }
    });
  };
  const reallyRemoveFolder = async () => {
    if (!confirmFolder) return;
    setBusy(true); setConfirmErr(null);
    try {
      await deleteCloseoutFolder(projectId, confirmFolder.id, true);
      setPath((p) => p.filter((x) => x !== confirmFolder.id));
      setConfirmFolder(null);
      await load();
    } catch (e) {
      setConfirmErr(e.message);
      // Reload even on failure: FN can delete the rows and still time out removing files, and the
      // screen must show what is really left.
      await load().catch(() => {});
    } finally { setBusy(false); }
  };
  const reallyRemoveDoc = async () => {
    const doc = confirmDoc;
    if (!doc) return;
    setBusy(true); setConfirmErr(null);
    try { await deleteCloseoutDoc(projectId, doc.id); setSelectedDocId(null); setConfirmDoc(null); await load(); } catch (e) { setConfirmErr(e.message); await load().catch(() => {}); } finally { setBusy(false); }
  };
  const folderPaths = useMemo(() => {
    const label = (f) => {
      const parts = [f.name];
      let cur = f.parentId ? folderById.get(f.parentId) : null;
      for (let i = 0; cur && i < 12; i++) { parts.unshift(cur.name); cur = cur.parentId ? folderById.get(cur.parentId) : null; }
      return parts.join(' / ');
    };
    return folders.map((f) => ({ id: f.id, path: label(f) })).sort((a, b) => a.path.localeCompare(b.path));
  }, [folders, folderById]);
  const doMove = (docId, folderId) => run(async () => { await moveCloseoutDoc(projectId, docId, folderId); setMovingDocId(null); await load(); });
  const addKind = (name) => run(async () => { await createCloseoutFolder(projectId, null, name); await load(); });

  /* ── render ── */
  let body;
  if (loading && !info) {
    body = <div className="co-emptybox"><Glyph d={GL.folder} size={26} />Loading closeout documents…</div>;
  } else if (loadErr) {
    body = <div className="co-pageerr"><span>{loadErr}</span><button type="button" className="co-btn" onClick={load}>Retry</button></div>;
  } else if (!info.link || changingLink) {
    body = canManage ? (
      <LinkCard projectId={projectId} project={project} suggestion={info.suggestion} current={changingLink ? info.link : null}
        onCancel={changingLink ? () => setChangingLink(false) : null}
        onLinked={() => { setChangingLink(false); setPath([]); setSelectedDocId(null); load(); }} />
    ) : info.error ? (
      <div className="co-pageerr"><span>{info.error.error}</span></div>
    ) : (
      <div className="co-emptybox"><Glyph d={GL.link} size={26} />This job isn’t linked to its Facility Navigator library yet. Ask the job’s PM to link it.</div>
    );
  } else if (info.error) {
    body = (
      <div className="co-pageerr">
        <span>{info.error.error}</span>
        <button type="button" className="co-btn" onClick={load}>Retry</button>
      </div>
    );
  } else {
    const isEmpty = folders.length === 0 && documents.length === 0;
    body = (
      <>
        <div className="co-strip">
          <span className="co-k">Facility Navigator</span>
          <b>{libName}</b>
          {canManage && <button type="button" className="co-linkbtn" onClick={() => setChangingLink(true)}>Change</button>}
          {documents.length > 0 && (<>
            <span className="co-msep" />
            <span><b>{documents.length}</b> document{documents.length === 1 ? '' : 's'}{totalBytes > 0 ? <> · <b>{formatBytes(totalBytes)}</b></> : null}</span>
          </>)}
          <span className="co-msp" />
          {documents.length > 0 && <span className="co-okpill"><Glyph d={GL.check} />Visible to customer</span>}
          {lastUpdated && <span className="co-ping">Updated {shortDate(lastUpdated)}</span>}
        </div>
        {kindTiles.length > 0 && (
          <CoverageCard
            tiles={kindTiles}
            selected={mode === 'browse' ? (path.length ? path[0] : null) : selectedKind}
            onSelect={(id) => {
              if (mode === 'handovers') { setSelectedKind(id); return; }
              setPath(id && id !== UNFILED ? [id] : []);
              setSelectedDocId(null);
            }}
            onAddKind={canManage ? addKind : undefined}
          />
        )}
        {scanning && (
          <div className="co-uplist"><div className="co-uplrow"><div className="co-un"><span>Reading the folder…</span><div className="co-bar"><i style={{ width: '100%', opacity: 0.35 }} /></div></div></div></div>
        )}
        <UploadRows projectId={projectId} />
        <SendProgress send={subsSend.send} error={subsSend.error} busy={subsSend.busy} onAgain={subsSend.again} onDismiss={subsSend.dismiss}
          note="It carries on if you leave this page." />
        {isEmpty ? (<>
          <div className={'co-emptybox' + (dropTarget === 'none' ? ' drop' : '')}
            onDragOver={dragOver('none')} onDragLeave={() => setDropTarget(null)} onDrop={(e) => onDropInto(e, null)}>
            <Glyph d={GL.file} size={26} />
            {canManage ? 'No documents yet — drag a folder of closeout documents here and its structure is kept, or:' : 'No closeout documents have been shared yet.'}
            {canManage && (
              <div className="co-emptybtns">
                <button type="button" className="co-btn" onClick={() => newFolder(null)} disabled={busy}><Glyph d={GL.folderPlus} />New folder</button>
                <button type="button" className="co-btn" onClick={() => pickInto(null, 'folder')}><Glyph d={GL.folder} />Upload folder</button>
                <button type="button" className="co-btn" onClick={() => pickInto(null, 'files')}><Glyph d={GL.upload} />Upload files</button>
                {info.fromSubs && <button type="button" className="co-btn" onClick={() => setFromSubs(true)}><Glyph d={GL.users} />Add from the subs</button>}
              </div>
            )}
          </div>
          {canManage && fromSubs && (
            <SubsDocumentsPicker projectId={projectId} folderName={libName} sending={subsSend} onClose={() => setFromSubs(false)}
              onAdd={(items) => subsSend.start(items, null).then((sent) => { if (sent) setFromSubs(false); })} />
          )}
        </>) : (
          <>
            <div className="co-viewtabs" role="tablist">
              <button type="button" role="tab" aria-selected={mode === 'browse'} className={mode === 'browse' ? 'on' : ''} onClick={() => setMode('browse')}>Browse</button>
              <button type="button" role="tab" aria-selected={mode === 'handovers'} className={mode === 'handovers' ? 'on' : ''} onClick={() => setMode('handovers')}>Upload history</button>
            </div>
            {mode === 'handovers' ? (
              <>
                <div className="co-hovhead">
                  <b>{selectedKind ? (selectedKind === UNFILED ? 'Unfiled' : ((kindFolderById.get(selectedKind) || {}).name || 'Documents')) : 'All uploads'}</b>
                  <span>{selectedKind ? `${shownDocs.length} document${shownDocs.length === 1 ? '' : 's'}` : 'newest first'}</span>
                  {selectedKind && <button type="button" className="co-linkbtn" style={{ marginLeft: 'auto' }} onClick={() => setSelectedKind(null)}>Show everything</button>}
                </div>
                {handovers.length > 0 ? (
                  <HandoverList handovers={handovers} byId={kindFolderById} showKind={!selectedKind}
                    onOpen={(d) => { setSelectedDocId(d.id); setQuickLook(true); }} onDownload={download} />
                ) : (
                  searchHits
                    ? <div className="co-hovplaceholder"><b>{noMatchText(search).title}</b><span>{noMatchText(search).body}</span></div>
                    : <div className="co-hovplaceholder"><b>Nothing uploaded here yet</b><span>It will appear here, by date, once it arrives.</span></div>
                )}
              </>
            ) : (
              <>
                <div className="co-cbar">
                  <div className="co-crumbs">
                    <button type="button" className={'co-crumb' + (path.length === 0 ? ' last' : '')} onClick={() => { setPath([]); setSelectedDocId(null); }}>{libName}</button>
                    {path.map((id, i) => (
                      <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span className="co-crumbsep"><Glyph d={GL.chevron} size={12} /></span>
                        <button type="button" className={'co-crumb' + (i === path.length - 1 ? ' last' : '')}
                          onClick={() => { setPath(path.slice(0, i + 1)); setSelectedDocId(null); }}>{(folderById.get(id) || {}).name || '…'}</button>
                      </span>
                    ))}
                  </div>
                  {canManage && (
                    <div className="co-headbtns">
                      <button type="button" className="co-btn" disabled={busy} onClick={() => newFolder(currentFolderId)}><Glyph d={GL.folderPlus} />New folder</button>
                      <button type="button" className="co-btn" onClick={() => pickInto(currentFolderId, 'files')}><Glyph d={GL.upload} />Upload files</button>
                      <button type="button" className="co-btn" onClick={() => pickInto(currentFolderId, 'folder')}><Glyph d={GL.folder} />Upload folder</button>
                      {info.fromSubs && (
                        <button type="button" className={'co-btn' + (fromSubs ? ' primary' : '')} onClick={() => setFromSubs((v) => !v)}>
                          <Glyph d={GL.users} />Add from the subs
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {canManage && fromSubs && (
                  <SubsDocumentsPicker projectId={projectId} folderName={currentFolderId ? ((folderById.get(currentFolderId) || {}).name || 'this folder') : libName}
                    sending={subsSend} onClose={() => setFromSubs(false)}
                    onAdd={(items) => subsSend.start(items, currentFolderId).then((sent) => { if (sent) setFromSubs(false); })} />
                )}
                <div className="co-browser">
                  <div className="co-cols">
                    {columns.map((col, ci) => {
                      const empty = col.folders.length === 0 && col.docs.length === 0;
                      const dropKey = col.parentId || 'none';
                      return (
                        <div className="co-col" key={col.parentId || 'root'}>
                          <div className="co-colhead"><b title={col.title}>{ci === 0 ? 'Kinds' : col.title}</b><span>{col.folders.length + col.docs.length} items</span></div>
                          <div role="listbox" aria-label={col.title} className={'co-colbody' + (dropTarget === dropKey ? ' drop' : '')}
                            onDragOver={dragOver(dropKey)} onDragLeave={() => setDropTarget((t) => (t === dropKey ? null : t))} onDrop={(e) => onDropInto(e, col.parentId)}>
                            {col.folders.map((f) => {
                              const onPath = path[ci] === f.id;
                              return renaming && renaming.id === f.id ? (
                                <div key={f.id} style={{ display: 'flex', gap: 6, padding: '2px 0' }}>
                                  <input className="co-txt" autoFocus value={renaming.text} disabled={busy} aria-label="Folder name"
                                    onFocus={(e) => e.currentTarget.select()} style={{ height: 30, minWidth: 0, flex: 1 }}
                                    onChange={(e) => setRenaming({ id: f.id, text: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenaming(null); }} />
                                  <button type="button" className="co-btn" style={{ height: 30 }} disabled={busy} onClick={submitRename}>Save</button>
                                </div>
                              ) : (
                                <button type="button" key={f.id} role="option" aria-selected={onPath}
                                  className={'co-crow' + (onPath ? ' onpath' : '') + (dropTarget === f.id ? ' dropinto' : '')}
                                  onClick={() => openFolder(ci, f.id)}
                                  onDoubleClick={() => canManage && setRenaming({ id: f.id, text: f.name })}
                                  onDragOver={dragOver(f.id)} onDragLeave={() => setDropTarget((t) => (t === f.id ? null : t))} onDrop={(e) => onDropInto(e, f.id)}>
                                  <span className="co-cico"><Glyph d={ci === 0 ? kindGlyph(f.name) : GL.folder} size={16} /></span>
                                  <EndName text={f.name} />
                                  <span className="co-cchip">{childCount(f.id)}</span>
                                  <span className="co-cchev"><Glyph d={GL.chevron} size={13} /></span>
                                </button>
                              );
                            })}
                            {col.docs.map((d) => (
                              <button type="button" key={d.id} role="option" aria-selected={selectedDocId === d.id} data-docrow="1"
                                className={'co-crow' + (selectedDocId === d.id ? ' onpath' : '')} onClick={() => openDoc(ci, d.id)}>
                                <span className="co-cico"><Glyph d={fileGlyph(d.fileName, d.contentType)} size={16} /></span>
                                <MidName text={d.fileName} />
                                <UploaderChip doc={d} />
                              </button>
                            ))}
                            {empty && <div className="co-colempty"><Glyph d={GL.folder} size={22} />{searchHits ? 'No matches' : ci === 0 ? 'No kinds yet' : 'Empty folder'}</div>}
                          </div>
                          {canManage && (
                            <div className="co-colfoot">
                              <button type="button" disabled={busy} onClick={() => newFolder(col.parentId)}><Glyph d={GL.folderPlus} size={13} />{ci === 0 ? 'New kind' : 'New folder'}</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {selectedDoc && (
                    <div className="co-detail">
                      {preview && heavy && !shownPreview ? (
                        <button type="button" className="co-dprev heavy" onClick={() => setQuickLook(true)} title={`Preview ${selectedDoc.fileName}`}>
                          <Glyph d={fileGlyph(selectedDoc.fileName, selectedDoc.contentType)} size={22} />
                          <span>Preview<em>{formatBytes(selectedDoc.sizeBytes)} — loads on open</em></span>
                        </button>
                      ) : preview ? (
                        <button type="button" className="co-dprev" onClick={() => setQuickLook(true)} aria-label={`Preview ${selectedDoc.fileName}`}>
                          {!shownPreview ? <span className="co-dprevwait">Loading preview…</span>
                            : preview === 'image' ? <img src={shownPreview} alt="" />
                              : preview === 'video' ? <video key={selectedDoc.id} src={shownPreview} muted playsInline preload="metadata" tabIndex={-1} />
                                : <iframe key={selectedDoc.id} title="" tabIndex={-1} scrolling="no" src={`${shownPreview}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} />}
                          <span className="co-dprevhint"><Glyph d={GL.expand} size={12} />Space</span>
                        </button>
                      ) : (
                        <div className="co-dtile"><Glyph d={fileGlyph(selectedDoc.fileName, selectedDoc.contentType)} size={22} /></div>
                      )}
                      {renamingDoc && renamingDoc.id === selectedDoc.id ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input className="co-txt" autoFocus value={renamingDoc.text} disabled={busy} aria-label="File name" style={{ flex: 1, minWidth: 0 }}
                            onFocus={(e) => { const v = e.currentTarget.value; const dot = v.lastIndexOf('.'); e.currentTarget.setSelectionRange(0, dot > 0 ? dot : v.length); }}
                            onChange={(e) => setRenamingDoc({ id: selectedDoc.id, text: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') submitDocRename(); if (e.key === 'Escape') setRenamingDoc(null); }} />
                          <button type="button" className="co-btn" disabled={busy} onClick={submitDocRename}>Save</button>
                        </div>
                      ) : (
                        <div className="co-dname">{selectedDoc.fileName}</div>
                      )}
                      <div className="co-drule" />
                      <div className="co-dfield"><b>Size</b><span>{formatBytes(selectedDoc.sizeBytes) || '—'}</span></div>
                      <div className="co-dfield"><b>Uploaded</b><span>{shortDate(selectedDoc.createdAt)}</span></div>
                      <div className="co-dfield"><b>By</b><span>{selectedDoc.uploadedByName || '—'}<UploaderChip doc={selectedDoc} /></span></div>
                      <div className="co-dfield"><b>Folder</b><span>{selectedDoc.folderId ? ((folderById.get(selectedDoc.folderId) || {}).name || '—') : ROOT_FOLDER_LABEL}</span></div>
                      {selectedDoc.description && <div className="co-dfield"><b>Note</b><span>{selectedDoc.description}</span></div>}
                      {preview && <button type="button" className="co-btn primary wide" onClick={() => setQuickLook(true)}><Glyph d={GL.expand} />View</button>}
                      <button type="button" className={'co-btn wide' + (preview ? '' : ' primary')} onClick={() => download(selectedDoc)}><Glyph d={GL.download} />Download</button>
                      {canManage && (
                        <div className="co-dactions">
                          <button type="button" className="co-btn" disabled={busy} onClick={() => setRenamingDoc({ id: selectedDoc.id, text: selectedDoc.fileName })}><Glyph d={GL.pencil} />Rename</button>
                          <button type="button" className="co-btn" disabled={busy} onClick={() => setMovingDocId((m) => (m === selectedDoc.id ? null : selectedDoc.id))}>Move…</button>
                          <span style={{ flex: 1 }} />
                          <button type="button" className="co-iconbtn" title="Delete document" aria-label={`Delete ${selectedDoc.fileName}`} disabled={busy}
                            onClick={() => { setConfirmErr(null); setConfirmDoc(selectedDoc); }}><Glyph d={GL.trash} /></button>
                        </div>
                      )}
                      {canManage && movingDocId === selectedDoc.id && (
                        <div style={{ marginTop: 10 }}>
                          <label htmlFor="co-move" className="co-k" style={{ display: 'block', marginBottom: 5 }}>Move to</label>
                          <select id="co-move" className="co-sel" style={{ width: '100%' }} disabled={busy} defaultValue={selectedDoc.folderId || ''}
                            onChange={(e) => doMove(selectedDoc.id, e.target.value || null)}>
                            <option value="">{ROOT_FOLDER_LABEL}</option>
                            {folderPaths.map((f) => <option key={f.id} value={f.id}>{f.path}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                  {!selectedDoc && canManage && currentFolderId && (
                    <div className="co-detail">
                      <div className="co-dtile"><Glyph d={GL.folder} size={22} /></div>
                      <div className="co-dname">{(folderById.get(currentFolderId) || {}).name || '—'}</div>
                      <div className="co-drule" />
                      <div className="co-dfield"><b>Items</b><span>{childCount(currentFolderId)}</span></div>
                      <div className="co-dactions" style={{ marginTop: 14 }}>
                        <button type="button" className="co-btn" disabled={busy} onClick={() => setRenaming({ id: currentFolderId, text: (folderById.get(currentFolderId) || {}).name || '' })}><Glyph d={GL.pencil} />Rename</button>
                        <span style={{ flex: 1 }} />
                        <button type="button" className="co-iconbtn" title="Delete folder" aria-label="Delete folder" disabled={busy} onClick={() => removeFolder(currentFolderId)}><Glyph d={GL.trash} /></button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </>
    );
  }

  const linked = !!(info && info.link && !changingLink && !info.error);
  return (
    <div className="co-tab">
      <style>{CSS}</style>
      <div className="co-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="co-title">Closeout documents</div>
          <div className="co-sub">
            {canManage
              ? <>As-builts, O&amp;M manuals, warranties and start-up sheets, kept in Facility Navigator — <b>shared with the customer</b> as soon as you upload them.</>
              : <>The job’s closeout documents, kept in Facility Navigator.</>}
          </div>
        </div>
        {linked && (
          <div className="co-headbtns">
            {documents.length > 0 && (
              <button type="button" className="co-btn primary" disabled={zipBusy} onClick={downloadAll}>
                <Glyph d={GL.download} />{zipBusy ? 'Preparing…' : `Download all${totalBytes > 0 ? ` · ${formatBytes(totalBytes)}` : ''}`}
              </button>
            )}
            <a className="co-btn" href={FN_CLOSEOUT_URL} target="_blank" rel="noopener noreferrer"><Glyph d={GL.external} />Open in Facility Navigator</a>
            <button type="button" className="co-btn" onClick={load} disabled={loading}><Glyph d={GL.refresh} />{loading ? 'Loading…' : 'Refresh'}</button>
          </div>
        )}
      </div>
      {actionErr && (
        <div className="co-pageerr" style={{ marginBottom: 14 }}>
          <span>{actionErr}</span>
          <button type="button" className="co-btn" onClick={() => setActionErr(null)}>Dismiss</button>
        </div>
      )}
      {body}

      {quickLook && selectedDoc && preview && (
        <div className="co-qlback" role="dialog" aria-modal="true" aria-label={selectedDoc.fileName} onClick={() => setQuickLook(false)}>
          <div className="co-qlwin" onClick={(e) => e.stopPropagation()}>
            <div className="co-qlbar">
              <Glyph d={fileGlyph(selectedDoc.fileName, selectedDoc.contentType)} size={17} />
              <div className="co-qlmeta">
                <b title={selectedDoc.fileName}>{selectedDoc.fileName}</b>
                <span>{[formatBytes(selectedDoc.sizeBytes), selectedDoc.uploadedByName ? `uploaded by ${selectedDoc.uploadedByName}` : null].filter(Boolean).join(' · ')}</span>
              </div>
              <button type="button" className="co-btn" onClick={() => download(selectedDoc)}><Glyph d={GL.download} />Download</button>
              <button type="button" className="co-iconbtn plain" aria-label="Close preview" onClick={() => setQuickLook(false)}><Glyph d={GL.close} /></button>
            </div>
            <div className="co-qlbody">
              {!shownPreview ? <span className="co-dprevwait">Loading…</span>
                : preview === 'image' ? <img src={shownPreview} alt={selectedDoc.fileName} />
                  : preview === 'video' ? <video key={selectedDoc.id} src={shownPreview} controls autoPlay playsInline aria-label={selectedDoc.fileName} />
                    : <iframe key={selectedDoc.id} title={selectedDoc.fileName} src={shownPreview} />}
            </div>
            <div className="co-qlfoot">
              <span>{siblingDocs.length > 1 ? `${siblingDocs.findIndex((d) => d.id === selectedDoc.id) + 1} of ${siblingDocs.length} · ← → to step through` : ' '}</span>
              <span>esc to close</span>
            </div>
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
        onChange={(e) => { uploadPicked(pickedFromInput(e.target.files), uploadTargetRef.current); e.target.value = ''; }} />
      <input ref={folderRef} type="file" multiple style={{ display: 'none' }} webkitdirectory=""
        onChange={(e) => { uploadPicked(pickedFromInput(e.target.files), uploadTargetRef.current); e.target.value = ''; }} />

      <DangerDialog open={!!confirmFolder} title="Delete this folder and everything in it?" token={(confirmFolder && confirmFolder.name) || ''}
        confirmLabel="Delete permanently" busy={busy} error={confirmErr} onClose={() => { setConfirmFolder(null); setConfirmErr(null); }} onConfirm={reallyRemoveFolder}
        warning={confirmFolder && (
          <>
            <b>{confirmFolder.name}</b> holds <b>{confirmFolder.documents}</b> document{confirmFolder.documents === 1 ? '' : 's'}
            {confirmFolder.folders > 0 && <> across <b>{confirmFolder.folders}</b> subfolder{confirmFolder.folders === 1 ? '' : 's'}</>}.
            {' '}All of them will be <b>permanently deleted</b> from Facility Navigator, for the customer too.
            <div style={{ marginTop: 10 }}>The files cannot be recovered.</div>
          </>
        )} />
      <DangerDialog open={!!confirmDoc} title="Delete this document?" token="delete" confirmLabel="Delete permanently" busy={busy} error={confirmErr}
        onClose={() => { setConfirmDoc(null); setConfirmErr(null); }} onConfirm={reallyRemoveDoc}
        warning={confirmDoc && (
          <>
            <b>{confirmDoc.fileName}</b> will be permanently deleted from Facility Navigator, and removed for the customer.
            <div style={{ marginTop: 10 }}>The file cannot be recovered.</div>
          </>
        )} />
    </div>
  );
}

/* FN's closeout.css, renamed under .co-tab (Summit has its own global .btn, .bar, .detail…). Metrics
   are FN's design handoff: 248px columns, 36px rows, 320px details pane. */
const CSS = `
.co-tab { font-family: var(--font-sans); color: var(--text-1); }
.co-tab .co-head { display: flex; align-items: flex-end; gap: 20px; margin-bottom: 18px; flex-wrap: wrap; }
.co-tab .co-title { font-size: 20px; font-weight: 600; letter-spacing: -.02em; color: var(--text-1); }
.co-tab .co-sub { margin-top: 6px; font-size: 13.5px; line-height: 1.55; color: var(--text-3); max-width: 760px; }
.co-tab .co-sub b { color: var(--text-2); font-weight: 600; }
.co-tab .co-headbtns { display: flex; gap: 8px; flex: none; align-items: center; flex-wrap: wrap; }
.co-tab .co-btn { appearance: none; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; height: 34px; padding: 0 13px; border: 1px solid var(--border-default); border-radius: 9px; background: var(--bg-surface); color: var(--text-2); font: inherit; font-size: 12.5px; font-weight: 600; white-space: nowrap; transition: .13s; text-decoration: none; }
.co-tab .co-btn:hover:not(:disabled) { background: var(--bg-surface-2); border-color: var(--border-strong); color: var(--text-1); }
.co-tab .co-btn:disabled { opacity: .55; cursor: default; }
.co-tab .co-btn svg { opacity: .85; flex: none; }
.co-tab .co-btn.primary { background: var(--accent); border-color: var(--accent); color: var(--on-accent, #fff); }
.co-tab .co-btn.primary:hover:not(:disabled) { background: var(--accent-hover); border-color: var(--accent-hover); color: var(--on-accent, #fff); }
.co-tab .co-btn.primary svg { opacity: 1; }
.co-tab .co-btn.danger { background: var(--danger); border-color: var(--danger); color: #fff; }
.co-tab .co-btn.danger:hover:not(:disabled) { background: var(--danger); border-color: var(--danger); color: #fff; filter: brightness(1.05); }
.co-tab .co-btn.wide { width: 100%; justify-content: center; margin-top: 14px; }
.co-tab .co-iconbtn { appearance: none; cursor: pointer; display: inline-grid; place-items: center; width: 32px; height: 32px; flex: none; border: 1px solid var(--border-default); border-radius: 9px; background: var(--bg-surface); color: var(--text-3); transition: .13s; font: inherit; }
.co-tab .co-iconbtn:hover:not(:disabled) { background: var(--danger-soft); border-color: color-mix(in oklab, var(--danger) 40%, var(--border-default)); color: var(--danger); }
.co-tab .co-iconbtn.plain:hover { background: var(--bg-hover); border-color: var(--border-strong); color: var(--text-1); }
.co-tab .co-iconbtn:disabled { opacity: .45; cursor: default; }
.co-tab .co-linkbtn { appearance: none; border: none; background: none; padding: 0; font: inherit; font-size: 12.5px; font-weight: 600; color: var(--accent-text); cursor: pointer; text-decoration: underline; text-underline-offset: 2px; }
.co-tab .co-sel, .co-tab .co-txt { height: 34px; border: 1px solid var(--border-default); border-radius: 9px; background: var(--bg-surface); color: var(--text-1); font: inherit; font-size: 12.5px; font-weight: 600; padding: 0 10px; cursor: pointer; }
.co-tab .co-txt { font-weight: 500; cursor: text; }
.co-tab .co-sel:focus, .co-tab .co-txt:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
.co-tab .co-k { font-size: 10.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--text-4); white-space: nowrap; }

.co-tab .co-strip { display: flex; align-items: center; gap: 12px; padding: 10px 16px; border: 1px solid var(--border-default); border-radius: 12px; background: var(--bg-surface); box-shadow: var(--shadow-sm); margin-bottom: 18px; flex-wrap: wrap; font-size: 13px; color: var(--text-3); font-weight: 500; }
.co-tab .co-strip b { color: var(--text-1); font-weight: 700; }
.co-tab .co-msep { width: 1px; height: 16px; background: var(--border-default); }
.co-tab .co-msp { flex: 1; }
.co-tab .co-ping { font-size: 12px; color: var(--text-4); font-variant-numeric: tabular-nums; }
.co-tab .co-okpill { display: inline-flex; align-items: center; gap: 5px; font-size: 10.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--success); }

.co-tab .co-linkcard { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding: 16px 18px; border: 1px solid var(--border-default); border-radius: 14px; background: var(--bg-surface); box-shadow: var(--shadow-sm); }
.co-tab .co-linkico { width: 38px; height: 38px; border-radius: 10px; display: grid; place-items: center; flex: none; background: var(--accent-soft); color: var(--accent); }
.co-tab .co-linktitle { font-size: 14px; font-weight: 600; color: var(--text-1); }
.co-tab .co-linksub { margin-top: 3px; font-size: 12.5px; line-height: 1.5; color: var(--text-3); }
.co-tab .co-inlineerr { margin-top: 6px; font-size: 12.5px; font-weight: 600; color: var(--danger); }

.co-tab .co-uplist { display: flex; flex-direction: column; gap: 10px; margin-bottom: 14px; }
.co-tab .co-upbatch { border: 1px solid var(--border-default); border-radius: 12px; background: var(--bg-surface); padding: 12px 14px; display: flex; flex-direction: column; gap: 9px; }
.co-tab .co-uphead { display: flex; align-items: baseline; gap: 10px; font-size: 12.5px; color: var(--text-3); }
.co-tab .co-uphead b { color: var(--text-1); font-weight: 700; }
.co-tab .co-uphead .co-linkbtn { margin-left: auto; }
.co-tab .co-uplrow { display: flex; align-items: center; gap: 11px; }
.co-tab .co-un { flex: 1; min-width: 0; }
.co-tab .co-un > span { display: block; font-size: 12.5px; color: var(--text-1); font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.co-tab .co-bar { height: 5px; border-radius: 3px; background: var(--bg-hover); margin-top: 5px; overflow: hidden; }
.co-tab .co-bar i { display: block; height: 100%; border-radius: 3px; background: var(--accent); transition: width .2s ease; }
.co-tab .co-bar.err i { background: var(--danger); }
.co-tab .co-uplpct { font-size: 11.5px; color: var(--text-3); font-weight: 600; min-width: 52px; text-align: right; font-variant-numeric: tabular-nums; }
.co-tab .co-uplpct.err { color: var(--danger); }
.co-tab .co-uplerr { margin-top: 4px; font-size: 12px; color: var(--danger); font-weight: 500; line-height: 1.45; }
.co-tab .co-uplerr button { margin-left: 8px; background: none; border: none; padding: 0; color: var(--accent-text); font: inherit; font-size: 12px; text-decoration: underline; cursor: pointer; }

.co-tab .co-emptybox { border: 1px solid var(--border-default); border-radius: 16px; background: var(--bg-surface); box-shadow: var(--shadow-sm); padding: 44px 20px; text-align: center; color: var(--text-3); font-size: 13.5px; font-weight: 500; margin-bottom: 18px; }
.co-tab .co-emptybox.drop { outline: 1.5px dashed var(--accent); outline-offset: -8px; background: var(--accent-soft); color: var(--accent-text); }
.co-tab .co-emptybox > svg { width: 26px; height: 26px; color: var(--text-4); display: block; margin: 0 auto 10px; }
.co-tab .co-emptybtns { display: flex; gap: 10px; justify-content: center; margin-top: 16px; flex-wrap: wrap; }
.co-tab .co-pageerr { border: 1px solid color-mix(in oklab, var(--danger) 45%, var(--border-default)); border-radius: 14px; background: var(--danger-soft); color: var(--danger); padding: 15px 17px; font-size: 13px; font-weight: 600; display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; }

.co-tab .co-viewtabs { display: inline-flex; gap: 2px; padding: 2px; border: 1px solid var(--border-default); border-radius: 9px; background: var(--bg-surface-2); margin-bottom: 14px; }
.co-tab .co-viewtabs button { padding: 5px 12px; border: none; border-radius: 7px; background: none; color: var(--text-3); font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.co-tab .co-viewtabs button.on { background: var(--bg-surface); color: var(--text-1); box-shadow: var(--shadow-sm); }

.co-tab .co-cbar { display: flex; align-items: center; gap: 14px; margin-bottom: 12px; flex-wrap: wrap; }
.co-tab .co-crumbs { display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1; font-size: 12.5px; color: var(--text-3); }
.co-tab .co-crumb { appearance: none; border: none; background: none; padding: 2px 4px; border-radius: 6px; font: inherit; font-size: 12.5px; color: var(--text-3); cursor: pointer; white-space: nowrap; max-width: 220px; overflow: hidden; text-overflow: ellipsis; }
.co-tab .co-crumb:hover { background: var(--bg-hover); color: var(--text-1); }
.co-tab .co-crumb.last { color: var(--text-1); font-weight: 600; max-width: 440px; cursor: default; }
.co-tab .co-crumb.last:hover { background: none; }
.co-tab .co-crumbsep { flex: none; color: var(--text-4); display: inline-flex; }

.co-tab .co-browser { display: flex; height: max(520px, calc(100vh - 26rem)); border: 1px solid var(--border-default); border-radius: 16px; background: var(--bg-surface); box-shadow: var(--shadow-md); overflow: hidden; }
.co-tab .co-cols { display: flex; flex: 1; min-width: 0; overflow-x: auto; overflow-y: hidden; }
.co-tab .co-col { position: relative; width: max-content; min-width: 248px; max-width: 420px; flex: 0 1 auto; display: flex; flex-direction: column; min-height: 0; border-right: 1px solid var(--border-subtle); }
.co-tab .co-colhead { flex: none; height: 28px; display: flex; align-items: center; gap: 8px; padding: 0 10px; border-bottom: 1px solid var(--border-subtle); }
.co-tab .co-colhead b { flex: 1; min-width: 0; font-size: 10.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--text-4); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.co-tab .co-colhead span { flex: none; font-size: 10.5px; color: var(--text-4); font-variant-numeric: tabular-nums; }
.co-tab .co-colbody { flex: 1; min-height: 0; overflow-y: auto; padding: 6px; display: flex; flex-direction: column; gap: 2px; }
.co-tab .co-colbody.drop { outline: 1.5px dashed var(--accent); outline-offset: -6px; border-radius: 10px; }
.co-tab .co-crow { position: relative; display: flex; align-items: center; gap: 8px; height: 36px; flex: none; padding: 0 10px; border-radius: 8px; border: none; background: none; cursor: pointer; text-align: left; font: inherit; color: var(--text-1); transition: background .12s, color .12s; }
.co-tab .co-crow:hover { background: var(--bg-hover); }
.co-tab .co-crow.onpath { background: var(--accent-soft); color: var(--accent-text); }
.co-tab .co-crow.onpath::before { content: ""; position: absolute; left: 0; top: 6px; bottom: 6px; width: 3px; border-radius: 0 3px 3px 0; background: var(--accent); }
.co-tab .co-crow.onpath .co-cico { color: var(--accent); }
.co-tab .co-crow.dropinto { background: var(--accent-soft); box-shadow: inset 0 0 0 1.5px var(--accent); }
.co-tab .co-crow:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
.co-tab .co-cico { flex: none; display: inline-flex; color: var(--text-3); }
.co-tab .co-cchev { flex: none; display: inline-flex; color: var(--text-4); }
.co-tab .co-cchip { flex: none; font-size: 10px; font-weight: 700; color: var(--accent-text); background: var(--accent-soft); border-radius: 5px; padding: 2px 7px; font-variant-numeric: tabular-nums; }
.co-tab .co-crow.onpath .co-cchip { background: var(--bg-surface); }
.co-tab .co-upby { flex: none; display: inline-block; font-size: 10px; font-weight: 700; line-height: 1.5; border-radius: 5px; padding: 1px 6px; white-space: nowrap; }
.co-tab .co-upby.customer { color: color-mix(in oklab, var(--success) 70%, var(--text-1)); background: var(--success-soft); }
.co-tab .co-upby.partner { color: var(--warning-text, color-mix(in oklab, var(--warning) 75%, var(--text-1))); background: var(--warning-soft); }
.co-tab .co-dfield .co-upby, .co-tab .co-hovwho .co-upby { margin-left: 6px; vertical-align: 1px; }
.co-tab .co-cname { flex: 1; min-width: 0; display: flex; font-size: 13.5px; font-weight: 600; letter-spacing: -.01em; }
/* pre, not nowrap: a space at the split ("Roofing Warranty | (district).pdf") is trailing whitespace in
   its own box and nowrap drops it — FN's page shows "Roofing Warranty(district).pdf". */
.co-tab .co-cname i { font-style: normal; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: pre; }
.co-tab .co-cname u { text-decoration: none; flex: none; white-space: pre; }
.co-tab .co-colempty { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; color: var(--text-4); font-size: 12px; padding: 12px; text-align: center; }
.co-tab .co-colfoot { border-top: 1px solid var(--border-subtle); padding: 6px; }
.co-tab .co-colfoot button { display: inline-flex; align-items: center; gap: 6px; width: 100%; justify-content: center; height: 28px; padding: 0 8px; border: 1px dashed var(--border-default); border-radius: 8px; background: none; color: var(--text-3); font: inherit; font-size: 12px; font-weight: 600; cursor: pointer; }
.co-tab .co-colfoot button:hover:not(:disabled) { border-color: var(--accent); border-style: solid; color: var(--accent-text); background: var(--accent-soft); }
.co-tab .co-colfoot button:disabled { opacity: .5; cursor: default; }

.co-tab .co-detail { width: 320px; flex: 0 0 320px; border-left: 1px solid var(--border-default); padding: 16px; overflow-y: auto; background: var(--bg-surface); }
.co-tab .co-dtile { width: 44px; height: 44px; border-radius: 10px; display: grid; place-items: center; background: var(--accent-soft); color: var(--accent); margin-bottom: 12px; }
.co-tab .co-dname { font-size: 15px; font-weight: 700; color: var(--text-1); line-height: 1.35; word-break: break-word; }
.co-tab .co-drule { height: 1px; background: var(--border-subtle); margin: 14px 0; }
.co-tab .co-dfield { display: flex; align-items: baseline; gap: 12px; padding: 8px 0; }
.co-tab .co-dfield > b { flex: none; font-size: 10.5px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: var(--text-4); }
.co-tab .co-dfield > span { flex: 1; min-width: 0; text-align: right; font-size: 13px; font-weight: 500; color: var(--text-1); word-break: break-word; }
.co-tab .co-dactions { display: flex; gap: 8px; margin-top: 12px; }
.co-tab .co-dprev { position: relative; display: block; width: 100%; height: 132px; padding: 0; margin-bottom: 12px; border: 1px solid var(--border-default); border-radius: 10px; overflow: hidden; background: var(--bg-surface-2); cursor: zoom-in; transition: border-color .12s; }
.co-tab .co-dprev:hover { border-color: var(--accent); }
.co-tab .co-dprev img { width: 100%; height: 100%; object-fit: cover; display: block; }
.co-tab .co-dprev video { width: 100%; height: 100%; object-fit: cover; display: block; pointer-events: none; background: #000; }
.co-tab .co-dprev iframe { width: 250%; height: 250%; border: 0; display: block; transform: scale(.4); transform-origin: 0 0; pointer-events: none; background: #fff; }
.co-tab .co-dprev.heavy { height: auto; display: flex; align-items: center; gap: 10px; padding: 12px; cursor: pointer; color: var(--accent-text); text-align: left; }
.co-tab .co-dprev.heavy span { display: block; font-size: 12.5px; font-weight: 600; }
.co-tab .co-dprev.heavy em { display: block; font-style: normal; font-size: 11px; font-weight: 500; color: var(--text-3); }
.co-tab .co-dprevwait { position: absolute; inset: 0; display: grid; place-items: center; font-size: 12px; color: var(--text-4); }
.co-tab .co-dprevhint { position: absolute; right: 6px; bottom: 6px; display: flex; align-items: center; gap: 4px; padding: 2px 6px; border-radius: 7px; font-size: 10.5px; font-weight: 600; color: var(--text-2); background: color-mix(in oklab, var(--bg-surface) 86%, transparent); border: 1px solid var(--border-default); }

.co-tab .co-qlback { position: fixed; inset: 0; z-index: 1300; display: grid; place-items: center; padding: 32px; background: rgba(0,0,0,.55); }
.co-tab .co-qlwin { display: flex; flex-direction: column; width: min(1100px, 100%); height: min(88vh, 100%); border-radius: 14px; overflow: hidden; background: var(--bg-surface); border: 1px solid var(--border-default); }
.co-tab .co-qlbar { flex: none; display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-bottom: 1px solid var(--border-default); color: var(--text-3); }
.co-tab .co-qlmeta { flex: 1; min-width: 0; }
.co-tab .co-qlmeta b { display: block; font-size: 13.5px; font-weight: 600; color: var(--text-1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.co-tab .co-qlmeta span { display: block; font-size: 11.5px; color: var(--text-3); }
.co-tab .co-qlbody { position: relative; flex: 1; min-height: 0; display: grid; place-items: center; background: var(--bg-surface-2); }
.co-tab .co-qlbody iframe { width: 100%; height: 100%; border: 0; background: #fff; }
.co-tab .co-qlbody img { max-width: 100%; max-height: 100%; object-fit: contain; }
.co-tab .co-qlbody video { max-width: 100%; max-height: 100%; background: #000; }
.co-tab .co-qlfoot { flex: none; display: flex; align-items: center; justify-content: space-between; padding: 7px 12px; border-top: 1px solid var(--border-default); font-size: 11.5px; color: var(--text-4); }

.co-tab .co-covcard { border: 1px solid var(--border-default); border-radius: 16px; background: var(--bg-surface); box-shadow: var(--shadow-sm); padding: 16px 18px; margin-bottom: 18px; }
.co-tab .co-covhead { display: flex; align-items: baseline; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
.co-tab .co-covhead b { font-size: 13.5px; font-weight: 700; color: var(--text-1); }
.co-tab .co-covhead span { font-size: 12px; color: var(--text-4); }
.co-tab .co-covtiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(112px, 1fr)); gap: 8px; }
.co-tab .co-covtile { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; padding: 12px 12px 10px; border-radius: 11px; min-height: 84px; text-align: left; cursor: pointer; border: 1px solid var(--border-default); background: var(--bg-surface); color: inherit; font: inherit; transition: border-color .12s, background .12s; }
.co-tab .co-covtile:hover { border-color: var(--border-strong); }
.co-tab .co-covtile:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
.co-tab .co-covtile svg { color: var(--text-2); }
.co-tab .co-ctname { font-size: 12px; font-weight: 600; line-height: 1.25; color: var(--text-1); }
.co-tab .co-ctsub { margin-top: auto; font-size: 11px; font-variant-numeric: tabular-nums; color: var(--text-3); }
.co-tab .co-covtile.empty { border-style: dashed; background: transparent; }
.co-tab .co-covtile.empty svg { color: var(--text-4); }
.co-tab .co-covtile.empty .co-ctname { color: var(--text-3); }
.co-tab .co-covtile.empty .co-ctsub { color: var(--text-4); }
.co-tab .co-covtile.unfiled { border-color: var(--border-strong); background: var(--bg-surface-2); }
.co-tab .co-covtile.on, .co-tab .co-covtile.empty.on, .co-tab .co-covtile.unfiled.on { border-style: solid; border-color: var(--accent); background: var(--accent-soft); }
.co-tab .co-covtile.on svg { color: var(--accent); }
.co-tab .co-covtile.on .co-ctname { color: var(--accent-text); }
.co-tab .co-covtile.add { border-style: dashed; background: none; color: var(--text-4); justify-content: center; }
.co-tab .co-covtile.add svg { color: var(--text-4); }
.co-tab .co-covtile.add .co-ctname { color: var(--text-3); }
.co-tab .co-covtile.add:hover { border-style: solid; border-color: var(--accent); background: var(--accent-soft); }
.co-tab .co-covtile.add:hover svg, .co-tab .co-covtile.add:hover .co-ctname { color: var(--accent-text); }
.co-tab .co-covtile.add.editing { border-style: solid; border-color: var(--accent); justify-content: center; padding: 10px; }
.co-tab .co-covtile.add.editing input { width: 100%; min-width: 0; height: 30px; padding: 0 8px; border: 1px solid var(--border-default); border-radius: 7px; background: var(--bg-surface); color: var(--text-1); font: inherit; font-size: 12px; }

.co-tab .co-hovhead { display: flex; align-items: center; gap: 9px; margin: 0 0 10px 2px; }
.co-tab .co-hovhead b { font-size: 13.5px; font-weight: 700; color: var(--text-1); }
.co-tab .co-hovhead span { font-size: 12px; color: var(--text-4); }
.co-tab .co-hovlist { display: flex; flex-direction: column; gap: 14px; }
.co-tab .co-hov { display: grid; grid-template-columns: 150px 1fr; gap: 16px; }
.co-tab .co-hovgut { padding-top: 12px; }
.co-tab .co-hovdate { font-size: 13px; font-weight: 700; color: var(--text-1); font-variant-numeric: tabular-nums; }
.co-tab .co-hovwho { margin-top: 3px; font-size: 11.5px; color: var(--text-4); }
.co-tab .co-hovarea { margin-top: 6px; font-size: 11.5px; color: var(--text-3); }
.co-tab .co-hovcard { border: 1px solid var(--border-default); border-radius: 14px; background: var(--bg-surface); box-shadow: var(--shadow-sm); overflow: hidden; }
.co-tab .co-hovrow { display: flex; align-items: center; gap: 12px; padding: 9px 12px 9px 13px; border-top: 1px solid var(--border-subtle); }
.co-tab .co-hovrow:first-child { border-top: none; }
.co-tab .co-hovrow.readable { cursor: pointer; }
.co-tab .co-hovrow.readable:hover { background: var(--bg-surface-2); }
.co-tab .co-hovext { flex: none; width: 34px; text-align: center; padding: 4px 0; border-radius: 6px; background: var(--bg-surface-2); color: var(--text-3); font-family: var(--font-mono); font-size: 10px; font-weight: 600; }
.co-tab .co-hovname { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.co-tab .co-hovname > span:first-child { font-size: 13px; font-weight: 600; color: var(--text-1); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.co-tab .co-hovsub { margin-top: 1px; font-size: 11.5px; color: var(--text-4); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.co-tab .co-hovsize { flex: none; width: 62px; text-align: right; font-size: 12px; color: var(--text-4); font-variant-numeric: tabular-nums; }
.co-tab .co-hovact { flex: none; width: 86px; text-align: right; font: inherit; font-size: 12px; font-weight: 600; color: var(--text-2); background: none; border: none; padding: 0; cursor: pointer; }
.co-tab .co-hovact.read { color: var(--accent-text); }
.co-tab button.co-hovact:hover { text-decoration: underline; }
.co-tab .co-hovplaceholder { border: 1px dashed var(--border-default); border-radius: 14px; padding: 28px; display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: var(--text-3); }
.co-tab .co-hovplaceholder b { font-size: 15px; color: var(--text-1); }

.co-tab .co-dlgback { position: fixed; inset: 0; z-index: 10001; display: grid; place-items: center; padding: 24px; background: oklch(0.2 0.008 100 / 0.34); }
.co-tab .co-dlg { width: 440px; max-width: 100%; background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: 14px; box-shadow: var(--shadow-md); padding: 22px 22px 18px; }
.co-tab .co-dlgicon { width: 36px; height: 36px; border-radius: 9px; display: grid; place-items: center; background: var(--danger-soft); color: var(--danger); margin-bottom: 14px; }
.co-tab .co-dlgtitle { font-size: 17px; font-weight: 600; letter-spacing: -.015em; color: var(--text-1); }
.co-tab .co-dlgbody { margin-top: 8px; font-size: 14px; line-height: 1.5; color: var(--text-2); }
.co-tab .co-dlgbody b { color: var(--text-1); }
.co-tab .co-dlgtype { margin: 14px 0 6px; font-size: 13px; color: var(--text-2); }
.co-tab .co-dlgtype b { font-family: var(--font-mono); color: var(--text-1); }
.co-tab .co-dlgerr { margin-top: 10px; font-size: 13px; font-weight: 600; color: var(--danger); }
.co-tab .co-dlgbtns { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }

@media (max-width: 900px) {
  .co-tab .co-browser { flex-direction: column; height: auto; min-height: 420px; }
  .co-tab .co-cols { flex-direction: column; overflow-x: hidden; }
  .co-tab .co-cols > .co-col:not(:last-child) { display: none; }
  .co-tab .co-col { width: auto; max-width: none; flex: 1 1 auto; border-right: none; }
  .co-tab .co-detail { width: auto; flex: 1 1 auto; border-left: none; border-top: 1px solid var(--border-default); }
}
@media (max-width: 760px) {
  .co-tab .co-hov { grid-template-columns: 1fr; gap: 8px; }
  .co-tab .co-hovgut { padding-top: 0; }
}
`;
